import { Component, Inject, OnDestroy, OnInit, ViewContainerRef } from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialog as MatDialog,
	MatLegacyDialogRef as MatDialogRef,
} from "@angular/material/legacy-dialog";
import { debounceTimeAfterFirst } from "@app/_helpers/debounceAfterTime";
import { hasPermissionByKey } from "@app/_helpers/permission";
import { resolveTagDisplayColor } from "@app/_helpers/tag-color";
import { AppService } from "@app/app.service";
import { entitiesToArray } from "@datorama/akita";
import { Logger, Tag, TagsQuery, TagsService, TagType, UserSettingsQuery } from "@timeghost/web-api";
import { xorBy } from "lodash-es";
import { BehaviorSubject, combineLatest, Observable, Subject, throwError } from "rxjs";
import {
	catchError,
	debounceTime,
	distinctUntilChanged,
	filter,
	finalize,
	map,
	startWith,
	takeUntil,
} from "rxjs/operators";
import { DialogAnchor } from "../client-project-picker-dialog/client-project-picker-dialog.component";
import { CreateTagComponent } from "../create-tag/create-tag.component";

const UNIQ_ITEMS = <T>(uniq: T[], item: T, byProp: string) =>
	(byProp ? uniq.findIndex((y) => y[byProp] === item[byProp]) !== -1 : uniq.includes(item)) ? uniq : [...uniq, item];
const log = new Logger("TagPickerDialogComponent");
@Component({
	selector: "app-tag-picker-dialog",
	templateUrl: "./tag-picker-dialog.component.html",
	styleUrls: ["./tag-picker-dialog.component.scss"],
})
export class TagPickerDialogComponent implements OnInit, OnDestroy {
	private ondestroy$ = new Subject<void>();
	private initialSelectedIds = new Set<string>();
	ngOnDestroy() {
		this.ondestroy$.next();
		this.ondestroy$.complete();
	}
	private _isLoading = new BehaviorSubject<boolean>(true);
	readonly isLoading$ = this._isLoading.asObservable().pipe(distinctUntilChanged());
	get isLoading() {
		return this._isLoading.getValue();
	}
	set isLoading(val: boolean) {
		this._isLoading.next(val);
	}
	readonly workspace$createPermission = this.userSettingsQuery.select().pipe(
		map((userSettings) => {
			return hasPermissionByKey(userSettings, "groupsCanManageTags");
		})
	);
	searchinput = new UntypedFormControl("", [
		(ctrl) => {
			return () => {
				const { entities } = this.tagsQuery.getValue();
				return (ctrl.value as string)?.trim().length > 0
					? entitiesToArray(entities, {}).filter((x: Tag) => x.name === ctrl.value).length > 0
						? { errors: { tags: ["tags.exists"] } }
						: null
					: null;
			};
		},
	]);
	private _selectedTags = new BehaviorSubject<Tag[]>(null);
	readonly selectedTags$ = this._selectedTags.asObservable().pipe(distinctUntilChanged());
	readonly selected$change = this.selectedTags$.pipe(
		map((tags) => {
			return xorBy(tags, this.src.data.SelectedTags ?? [], (x) => x.id).length > 0;
		})
	);
	get selectedTags() {
		return this._selectedTags.getValue();
	}
	set selectedTags(val: Tag[]) {
		this._selectedTags.next(val.uniqBy((x) => x.id));
	}
	constructor(
		@Inject(MAT_DIALOG_DATA)
		private src: TagDialogData,
		private ref: MatDialogRef<TagPickerDialogComponent>,
		private view: ViewContainerRef,
		private dialog: MatDialog,
		private tagService: TagsService,
		private tagsQuery: TagsQuery,
		private userSettingsQuery: UserSettingsQuery,
		private appService: AppService
	) {}
	readonly lockedTagHint = "utils.locked-tag-hint";
	get tagType() {
		return this.src.data?.type;
	}
	data$filtered = combineLatest([
		this.tagsQuery.selectAll({ filterBy: (x) => x.tagType === this.tagType }),
		this.selectedTags$,
		(this.searchinput.valueChanges as Observable<string>).pipe(
			startWith(""),
			distinctUntilChanged(),
			debounceTime(200)
		),
	]).pipe(
		map(([tags, selected, search]): [Tag[], string] => {
			// project tags can be "selected" but not exist in the global tag store query result
			const selectedMissingFromStore = (this.src.data.SelectedTags ?? []).filter(
				(t) => !tags.some((x) => x.id === t.id)
			);
			const all = [...tags, ...selectedMissingFromStore].uniqBy((x) => x.id);
			const withSelected = all.map((x) => ({ ...x, selected: !!selected.find((y) => y.id === x.id) }));
			const sorted = withSelected.sort((a, b) => {
				// UX: keep list stable while toggling; only "initially selected" items are pinned to the top.
				const aPinned = this.initialSelectedIds.has(a.id);
				const bPinned = this.initialSelectedIds.has(b.id);
				if (aPinned !== bPinned) return aPinned ? -1 : 1;
				const an = (a.name ?? "").toLocaleLowerCase();
				const bn = (b.name ?? "").toLocaleLowerCase();
				return an.localeCompare(bn);
			});
			return [sorted, search];
		}),
		map(([tags, search]) => {
			return search && search.trim().length > 0
				? tags.filter((x) => x.name && x.name.toLowerCase().indexOf(search.toLowerCase()) > -1)
				: tags;
		})
	);

	tagDisplayColor(tag: Tag & { color?: string | null }) {
		return resolveTagDisplayColor(tag.color, tag.id, (n) => this.appService.colorScheme.getColorFromScheme(n));
	}

	isLocked(tag: Tag): boolean {
		const locked = this.src.data?.LockedTagIds ?? [];
		return locked.includes(tag.id);
	}

	showLockedHint() {
		this.appService.notifyInfo(this.lockedTagHint);
	}

	selectItem(tag: Tag) {
		if (this.isLocked(tag)) return;
		const tags = this.selectedTags.filter((x) => x.id === tag.id);
		if (this.src.data.canToggle && tags.length > 0) {
			this.selectedTags = this.selectedTags.filter((x) => x.id !== tag.id);
			return;
		}
		this.selectedTags = [...this.selectedTags, tag];
	}

	trackFiltered(obj: Tag) {
		return obj.id;
	}
	onCreateTag(ev: KeyboardEvent) {
		if (ev.ctrlKey && ev.code === "Enter") {
			this.isLoading = true;
			this.searchinput.disable();
			this.tagService
				.add({
					name: this.searchinput.value,
					tagType: this.tagType,
				})
				.pipe(
					catchError((err) => {
						return throwError(err);
					}),
					finalize(() => {
						this.searchinput.enable();
					})
				)
				.subscribe(
					(x) => {
						this.selectItem(x);
						this.isLoading = false;
						this.searchinput.enable();
					},
					(err) => {
						log.error("Error while processing request", err);
						this.isLoading = false;
						this.searchinput.enable();
					}
				);
		}
	}
	private syncSelectedTagsFromStore(tags: Tag[]) {
		const selected = this.selectedTags;
		if (!selected?.length) return;
		this.selectedTags = selected.map((s) => tags.find((t) => t.id === s.id) ?? s);
	}

	ngOnInit() {
		this.selectedTags = this.src.data.SelectedTags || [];
		this.initialSelectedIds = new Set((this.src.data.SelectedTags ?? []).map((t) => t.id));
		this.tagsQuery
			.selectAll({ filterBy: (x) => x.tagType === this.tagType })
			.pipe(takeUntil(this.ondestroy$))
			.subscribe((tags) => this.syncSelectedTagsFromStore(tags));
		this.ref._containerInstance._config.autoFocus = true;
		this.ref.addPanelClass("tag-picker-dialog-container");
		this.ref.updateSize("400px");
		this.ref.disableClose = true;
		this.ref
			.keydownEvents()
			.pipe(filter((x) => ["Escape"].includes(x.key)))
			.subscribe(() => this.closeDialog());
		this.ref.backdropClick().subscribe(() => this.closeDialog());
		this.tagsQuery
			.selectLoading()
			.pipe(distinctUntilChanged(), debounceTimeAfterFirst(100))
			.subscribe((x) => (this.isLoading = x));
	}
	closeDialog() {
		return this.ref.close(this.selectedTags?.map((x) => ({ ...x, tagType: x.tagType ?? this.tagType })) || []);
	}
	createTagDialog() {
		return this.dialog
			.open(CreateTagComponent, {
				data: {
					name: this.searchinput.value,
					type: this.tagType,
					allowTypeModify: this.src.data.allowTypeModify,
				},
			})
			.afterClosed()
			.pipe(filter((x) => !!x))
			.subscribe((x) => {
				this.selectItem(x);
			});
	}
}

export interface TagDialogData {
	anchor: DialogAnchor;
	data: {
		SelectedTags: Tag[];
		/** tag ids that should be visible but non-editable */
		LockedTagIds?: string[];
		type: TagType;
		canToggle: boolean;
		allowTypeModify?: boolean;
	};
}
